import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { MessageCircle, X, Send, Bot, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ChatMessage } from './types';
import { streamChat } from './chatService';
import ChatBubble from './ChatBubble';
import TypingIndicator from './TypingIndicator';
import WelcomeScreen from './WelcomeScreen';
import { useLanguage } from '@/contexts/LanguageContext';

const FinancialChatbot: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const { t, language } = useLanguage();

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      const el = scrollRef.current?.querySelector('[data-radix-scroll-area-viewport]');
      if (el) el.scrollTop = el.scrollHeight;
    });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  useEffect(() => {
    if (isOpen) setTimeout(() => inputRef.current?.focus(), 300);
  }, [isOpen]);

  const handleSend = useCallback(async (text?: string) => {
    const messageText = (text || input).trim();
    if (!messageText || isLoading) return;

    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: messageText,
      timestamp: new Date(),
    };

    const updated = [...messages, userMessage];
    setMessages(updated);
    setInput('');
    setIsLoading(true);

    abortRef.current = new AbortController();

    try {
      await streamChat(
        updated,
        (id) => {
          setMessages(prev => [...prev, {
            id, role: 'assistant', content: '', timestamp: new Date(),
          }]);
        },
        (id, content) => {
          setMessages(prev => prev.map(m => m.id === id ? { ...m, content } : m));
        },
        abortRef.current.signal,
        language
      );
    } catch (error) {
      if ((error as Error).name === 'AbortError') return;
      console.error('Chat error:', error);
      setMessages(prev => [...prev, {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: error instanceof Error ? error.message : 'Something went wrong. Please try again.',
        timestamp: new Date(),
        isError: true,
      }]);
    } finally {
      setIsLoading(false);
      abortRef.current = null;
    }
  }, [input, isLoading, messages, language]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  }, [handleSend]);

  const clearChat = useCallback(() => {
    abortRef.current?.abort();
    setMessages([]);
    setIsLoading(false);
  }, []);

  const handleInput = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 96) + 'px';
  }, []);

  const showTyping = isLoading && messages.length > 0 && messages[messages.length - 1]?.role !== 'assistant';

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className={cn(
          'fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300',
          'bg-primary text-primary-foreground hover:shadow-xl hover:scale-105 active:scale-95',
          isOpen && 'pointer-events-none opacity-0 scale-75'
        )}
        aria-label={t('chatbot.open')}
      >
        <MessageCircle className="h-6 w-6" />
      </button>

      <div
        className={cn(
          'fixed z-50 flex flex-col overflow-hidden transition-all duration-300 ease-out',
          'bottom-5 right-5 w-[380px] h-[540px] rounded-2xl border border-border shadow-2xl',
          'max-sm:inset-0 max-sm:w-full max-sm:h-full max-sm:rounded-none max-sm:border-0',
          'bg-card',
          isOpen
            ? 'opacity-100 translate-y-0 scale-100 pointer-events-auto'
            : 'opacity-0 translate-y-4 scale-95 pointer-events-none'
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-card">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
              <Bot className="h-4.5 w-4.5 text-primary" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-foreground leading-tight">{t('chatbot.title')}</h3>
              <p className="text-[11px] text-muted-foreground leading-tight">
                {isLoading ? t('chatbot.thinking') : t('chatbot.poweredBy')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-0.5">
            {messages.length > 0 && (
              <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground" onClick={clearChat} title={t('chatbot.clear')}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            )}
            <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground" onClick={() => setIsOpen(false)} aria-label="Close chat">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Messages */}
        <ScrollArea className="flex-1 overflow-hidden" ref={scrollRef}>
          <div className="px-4 py-3">
            {messages.length === 0 ? (
              <WelcomeScreen onSend={handleSend} disabled={isLoading} />
            ) : (
              <div className="space-y-4">
                {messages.map((msg) => (
                  <ChatBubble key={msg.id} message={msg} />
                ))}
                {showTyping && <TypingIndicator />}
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Input */}
        <div className="px-3 py-2.5 border-t border-border bg-card">
          <div className="flex items-end gap-2">
            <textarea
              ref={inputRef}
              value={input}
              onChange={handleInput}
              onKeyDown={handleKeyDown}
              placeholder={t('chatbot.placeholder')}
              className={cn(
                'flex-1 resize-none text-sm rounded-xl px-3.5 py-2.5 leading-snug',
                'border border-input bg-background text-foreground',
                'placeholder:text-muted-foreground/60',
                'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-0',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                'transition-colors'
              )}
              rows={1}
              style={{ height: '38px', maxHeight: '96px' }}
              disabled={isLoading}
            />
            <Button onClick={() => handleSend()} disabled={!input.trim() || isLoading} size="icon" className="h-[38px] w-[38px] rounded-xl flex-shrink-0" aria-label="Send message">
              <Send className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default FinancialChatbot;
